import type { SoundCategory } from '@/systems';

// Storage keys
const STORAGE_PREFIX = 'songko_game_';
const MASTER_VOLUME_KEY = `${STORAGE_PREFIX}master_volume`;
const MUTE_STATE_KEY = `${STORAGE_PREFIX}mute_state`;

// Category-specific volume keys
const getCategoryVolumeKey = (category: SoundCategory) => 
  `${STORAGE_PREFIX}volume_${category}`;

/**
 * Helper functions for storing and retrieving volume settings
 */
export const VolumeStorage = {
  /**
   * Save master volume to local storage
   */
  saveMasterVolume(volume: number): void {
    try {
      localStorage.setItem(MASTER_VOLUME_KEY, volume.toString());
    } catch (error) {
      console.warn('Failed to save master volume to local storage:', error);
    }
  },

  /**
   * Load master volume from local storage
   * @returns Saved volume or default value if not found
   */
  loadMasterVolume(defaultValue = 1.0): number {
    try {
      const saved = localStorage.getItem(MASTER_VOLUME_KEY);
      return saved !== null ? parseFloat(saved) : defaultValue;
    } catch (error) {
      console.warn('Failed to load master volume from local storage:', error);
      return defaultValue;
    }
  },

  /**
   * Save category-specific volume to local storage
   */
  saveCategoryVolume(category: SoundCategory, volume: number): void {
    try {
      localStorage.setItem(getCategoryVolumeKey(category), volume.toString());
    } catch (error) {
      console.warn(`Failed to save ${category} volume to local storage:`, error);
    }
  },

  /**
   * Load category-specific volume from local storage
   * @returns Saved volume or default value if not found
   */
  loadCategoryVolume(category: SoundCategory, defaultValue = 1.0): number {
    try {
      const saved = localStorage.getItem(getCategoryVolumeKey(category));
      return saved !== null ? parseFloat(saved) : defaultValue;
    } catch (error) {
      console.warn(`Failed to load ${category} volume from local storage:`, error);
      return defaultValue;
    }
  },

  /**
   * Save mute state to local storage
   */
  saveMuteState(muted: boolean): void {
    try {
      localStorage.setItem(MUTE_STATE_KEY, muted.toString());
    } catch (error) {
      console.warn('Failed to save mute state to local storage:', error);
    }
  },

  /**
   * Load mute state from local storage
   * @returns Saved mute state or default value if not found
   */
  loadMuteState(defaultValue = false): boolean {
    try {
      const saved = localStorage.getItem(MUTE_STATE_KEY);
      return saved !== null ? saved === 'true' : defaultValue;
    } catch (error) {
      console.warn('Failed to load mute state from local storage:', error);
      return defaultValue;
    }
  },
};
